import * as XLSX from 'xlsx'
import {
  getFlexyStockFilterValue,
  getProductTableBodegaColumns,
  getStockDisplayValueForBodegas,
  type FlexyProductStockRecord,
  type ProductTableStockFilterKey
} from './productTableBodegasConfig'

export interface CatalogProductForStockExport {
  sku: string
  name: string
  raw: Record<string, unknown>
}

export interface ProductsListPage {
  items: Record<string, unknown>[]
  totalPages: number | null
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function readPositiveInt(value: unknown): number | null {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 1) return null
  return Math.floor(parsed)
}

export function normalizeExportSku(raw: unknown): string {
  const sku = String(raw ?? '').trim()
  if (!sku) return ''
  return sku.replace(/^0+/, '') || sku
}

export function readCatalogSku(product: Record<string, unknown>): string {
  const raw = product.sku ?? product.Sku ?? product.IdProducto
  return String(raw ?? '').trim()
}

export function readCatalogName(product: Record<string, unknown>): string {
  const raw = product.name ?? product.nombre ?? product.NombreProducto ?? product.Nombre
  return String(raw ?? '').trim()
}

export function parseProductsListPage(data: unknown): ProductsListPage {
  if (!isRecord(data) || !Array.isArray(data.data)) {
    return { items: [], totalPages: null }
  }
  const items = data.data.filter(isRecord)
  const meta = isRecord(data.meta) ? data.meta : null
  const pagination = isRecord(data.pagination) ? data.pagination : null
  const totalPages = readPositiveInt(
    data.totalPages ?? data.last_page ?? meta?.last_page ?? meta?.totalPages ?? pagination?.totalPages
  )
  return { items, totalPages }
}

export function extractFlexyProductsFromExportResponse(
  data: unknown
): FlexyProductStockRecord[] {
  if (!isRecord(data)) return []
  if (Array.isArray(data.products)) return data.products.filter(isRecord)
  if (Array.isArray(data.productos)) return data.productos.filter(isRecord)
  return []
}

function buildFlexyProductMap(
  products: FlexyProductStockRecord[]
): Map<string, FlexyProductStockRecord> {
  const map = new Map<string, FlexyProductStockRecord>()
  for (const product of products) {
    const rawSku = String(product.sku ?? product.Sku ?? '').trim()
    if (!rawSku) continue
    map.set(rawSku, product)
    const normalized = normalizeExportSku(rawSku)
    if (normalized && normalized !== rawSku) {
      map.set(normalized, product)
    }
  }
  return map
}

function findFlexyProduct(
  map: Map<string, FlexyProductStockRecord>,
  sku: string
): FlexyProductStockRecord | undefined {
  return map.get(sku) ?? map.get(normalizeExportSku(sku))
}

export function buildProductStockFileName(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  const stamp = [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate())
  ].join('-')
  const time = `${pad(date.getHours())}-${pad(date.getMinutes())}`
  return `stock-productos_${stamp}_${time}.xlsx`
}

/**
 * Filas del Excel: catálogo completo y saldos Flexy recién consultados.
 * Las bodegas son todas las de la lista para el rol, no las del filtro visible.
 */
export function buildProductStockExportRows(
  catalog: CatalogProductForStockExport[],
  flexyProducts: FlexyProductStockRecord[],
  roleId: number | undefined | null
): Record<string, string | number>[] {
  const columns = getProductTableBodegaColumns(roleId)
  const filterKeys = columns.map((column) => column.filterKey)
  const validKeys = new Set<ProductTableStockFilterKey>(filterKeys)
  const flexyMap = buildFlexyProductMap(flexyProducts)

  const rows = catalog
    .filter((product) => product.sku.trim() !== '')
    .map((product) => {
      const flexyProduct = findFlexyProduct(flexyMap, product.sku)
      const row: Record<string, string | number> = {
        SKU: product.sku,
        Nombre: product.name,
        Stock: getStockDisplayValueForBodegas(
          flexyProduct,
          product.raw,
          filterKeys,
          validKeys
        )
      }
      for (const column of columns) {
        const qty = getFlexyStockFilterValue(flexyProduct, column.filterKey)
        row[column.header] = qty === null ? '' : qty
      }
      return row
    })

  rows.sort((left, right) =>
    String(left.SKU).localeCompare(String(right.SKU), 'es', { numeric: true })
  )
  return rows
}

export function writeProductStockWorkbook(
  rows: Record<string, string | number>[],
  fileName = buildProductStockFileName()
): void {
  const workbook = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(rows)
  const headers = rows[0] ? Object.keys(rows[0]) : []
  sheet['!cols'] = headers.map((header) => ({
    wch: header === 'Nombre' ? 42 : header === 'SKU' ? 22 : 16
  }))
  if (sheet['!ref']) {
    sheet['!autofilter'] = { ref: sheet['!ref'] }
  }
  XLSX.utils.book_append_sheet(workbook, sheet, 'Stock')
  XLSX.writeFile(workbook, fileName)
}
